/**
 * Builds ranked solar designs from the intake configuration.
 * Max-fills each selected orientation, applies shade/priority settings, then heuristics + Gemini + simulation.
 */
import { callGemini, GEMINI_MODEL_ID } from '../../services/geminiService';
import type { SavedRoofModulesLayout } from '../../@types/types';
import {
    collectIncludedModuleIds,
    requestPciMaxfillStrategiesFromEmbed,
    requestPciSimulateOptionsFromEmbed,
    type PciMaxfillSnapshot,
    type PciSimulatedOptionResult,
} from './preContractEmbedBridge';
import {
    clearPreContractFullModuleLayouts,
    persistPreContractFullModuleLayouts,
} from './preContractFullModuleLayouts';
import {
    countIncludedModules,
    estimateLayoutAnnualKwh,
    isPreContractGreyPanel,
    isPreContractPanelAllowed,
    isPreContractLowEfficiency,
    isPreContractPurplePanel,
    PRE_CONTRACT_GREY_SA_MIN,
    PRE_CONTRACT_LOW_EFFICIENCY_SA,
    PRE_CONTRACT_PURPLE_SA_MAX,
} from './preContractModuleLayout';
import type {
    PreContractFillOrientation,
    PreContractIntakeForm,
    PreContractModuleCell,
    PreContractRoofModuleLayout,
    PreContractSiteTree,
} from './preContractTypes';
import { normalizeFillOrientations } from './preContractTypes';
import treeCartService, {
    getTreeCalculatorBandsForTree,
    getTreeCutCostWithDefaults,
} from '../../services/treeCartService';

const HIGH_ACCESS_SA = 75;
const RELAXED_ACCESS_SA = 65;
/** Hard cap — AI layouts must not exceed this fraction of annual utility usage. */
const MAX_UTILITY_OFFSET_RATIO = 1.1;
/** Single recommended design for pre-contract intake (fast path). */
const MAX_OPTIONS = 2;
const GEMINI_DESIGN_TIMEOUT_MS = 25000;
const SPECIFIC_YIELD = 1250;

export type PreContractAiProgress = 'layout' | 'maxfill' | 'gemini' | 'simulate' | 'treecut';

/** Tree-cut extras attached to an AI option on the Tree Cutting results tab. */
export type PreContractAiTreeCut = {
    treeIds: string[];
    treeLabels: string[];
    cutCost: number | null;
    annualKwhGain: number | null;
    /** Tree Calculator DBH dropdown from the cut tree trunk. */
    dbhRange?: string | null;
    /** Tree Calculator height dropdown from the cut tree mesh. */
    heightRange?: string | null;
};

/** One AI-ranked layout the sales user can pick after Run AI. */
export type PreContractAiDesignOption = {
    id: string;
    rank: number;
    title: string;
    reason: string;
    layouts: PreContractRoofModuleLayout[];
    roofSelections: boolean[];
    moduleCount: number;
    systemKw: number;
    annualKwh: number;
    /** Utility production (pre-loss) when measured in OneDraw, else null. */
    utilityKwh?: number | null;
    averageSolarAccess: number;
    /** Efficiency (TSRF) from the live Production calc when simulated. */
    efficiencyTsrf?: number | null;
    /** System multiplier from the live Production calc or included-module average. */
    multiplier?: number | null;
    offsetPercent: number | null;
    roofLabels: string[];
    includesGrayPanels: boolean;
    includesGreyPanels?: boolean;
    includesPurplePanels?: boolean;
    /** Portrait / landscape / mixed max-fill this option was taken from. */
    fillOrientation?: PreContractFillOrientation;
    /** Exact 3D placement captured while simulating this option in the embed. */
    modulesLayout?: SavedRoofModulesLayout;
    /** Full max-fill grid for All modules (active + inactive panels on every roof). */
    fullModuleLayouts?: PreContractRoofModuleLayout[];
    /** Trees that should be ghosted when this option is selected. */
    ghostedTreeIds?: string[];
    /** Present on Tree Cutting tab options. */
    treeCut?: PreContractAiTreeCut;
};

export type PreContractAiDesignsResult = {
    layoutOptions: PreContractAiDesignOption[];
    treeCutOptions: PreContractAiDesignOption[];
};

type CandidateStrategy =
    | 'your-layout'
    | 'max-production'
    | 'usage-fit'
    | 'high-access'
    | 'best-roofs';

type RankedCandidate = PreContractAiDesignOption & {
    strategy: CandidateStrategy;
    score: number;
};

type GeminiPick = {
    id: string;
    title?: string;
    reason?: string;
    moduleIds?: string[];
    orientation?: PreContractFillOrientation;
};

function parseUsageKwh(raw: string): number {
    const n = Number(String(raw).replace(/,/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Annual usage from intake — the kWh the ranked layouts are trying to reach. */
export function parsePreContractTargetKwh(raw: string): number {
    return parseUsageKwh(raw);
}

/** Maximum allowed annual production when sizing AI layouts (110% of utility usage). */
export function parsePreContractMaxProductionKwh(raw: string): number {
    const target = parseUsageKwh(raw);
    return target > 0 ? Math.round(target * MAX_UTILITY_OFFSET_RATIO) : 0;
}

function maxAllowedProductionKwh(target: number): number {
    return target > 0 ? Math.round(target * MAX_UTILITY_OFFSET_RATIO) : Infinity;
}

function cloneLayouts(layouts: PreContractRoofModuleLayout[]): PreContractRoofModuleLayout[] {
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => (cell ? { ...cell } : null)),
    }));
}

function isGreyPanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractGreyPanel(cell.solarAccess);
}

function isPurplePanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractPurplePanel(cell.solarAccess);
}

function isGrayPanel(cell: NonNullable<PreContractModuleCell>): boolean {
    return isPreContractLowEfficiency(cell.solarAccess);
}

function collectPresentCells(
    layouts: PreContractRoofModuleLayout[],
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    const out: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> = [];
    layouts.forEach((layout) => {
        layout.modules.forEach((cell) => {
            if (cell) out.push({ roofIndex: layout.roofIndex, cell });
        });
    });
    return out;
}

function withIncludedIds(
    layouts: PreContractRoofModuleLayout[],
    includedIds: Set<string>,
    baseRoofSelections?: boolean[],
): { layouts: PreContractRoofModuleLayout[]; roofSelections: boolean[] } {
    const next = cloneLayouts(layouts).map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            return { ...cell, included: includedIds.has(cell.id) };
        }),
    }));
    // AI designs never keep a roof with fewer than 2 included modules.
    const MIN_MODULES_PER_ROOF = 2;
    next.forEach((layout) => {
        const includedCount = layout.modules.filter((cell) => cell?.included).length;
        if (includedCount > 0 && includedCount < MIN_MODULES_PER_ROOF) {
            layout.modules = layout.modules.map((cell) =>
                cell ? { ...cell, included: false } : null,
            );
        }
    });
    const roofSelections = next.map((layout) => {
        if (baseRoofSelections && baseRoofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => Boolean(cell?.included));
    });
    return { layouts: next, roofSelections };
}

function sumIncludedModuleProductionKwh(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number {
    let sum = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const production = Number(cell.productionKwh);
            if (Number.isFinite(production) && production > 0) sum += production;
        });
    });
    return sum > 0 ? Math.round(sum) : 0;
}

function applyLiveMetricsByModuleId(
    layouts: PreContractRoofModuleLayout[],
    saById: Record<string, number> | undefined,
    productionById: Record<string, number> | undefined,
    multiplierById: Record<string, number> | undefined,
): PreContractRoofModuleLayout[] {
    const saUpper: Record<string, number> = {};
    const prodUpper: Record<string, number> = {};
    const multUpper: Record<string, number> = {};
    if (saById) {
        Object.entries(saById).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                saUpper[key.trim().toUpperCase()] = value;
            }
        });
    }
    if (productionById) {
        Object.entries(productionById).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                prodUpper[key.trim().toUpperCase()] = Math.round(value);
            }
        });
    }
    if (multiplierById) {
        Object.entries(multiplierById).forEach(([key, value]) => {
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                multUpper[key.trim().toUpperCase()] = Math.round(value * 1000) / 1000;
            }
        });
    }
    if (
        Object.keys(saUpper).length === 0 &&
        Object.keys(prodUpper).length === 0 &&
        Object.keys(multUpper).length === 0
    ) {
        return layouts;
    }
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell || !cell.included) return cell;
            const key = String(cell.id).trim().toUpperCase();
            const solarAccess = saUpper[key];
            const productionKwh = prodUpper[key];
            const multiplier = multUpper[key];
            return {
                ...cell,
                ...(solarAccess != null ? { solarAccess } : {}),
                ...(productionKwh != null ? { productionKwh } : {}),
                ...(multiplier != null ? { multiplier } : {}),
            };
        }),
    }));
}

function applySolarAccessByModuleId(
    layouts: PreContractRoofModuleLayout[],
    saById: Record<string, number> | undefined,
): PreContractRoofModuleLayout[] {
    if (!saById) return layouts;
    const upper: Record<string, number> = {};
    Object.entries(saById).forEach(([key, value]) => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            upper[key.trim().toUpperCase()] = value;
        }
    });
    if (Object.keys(upper).length === 0) return layouts;
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            const sa = upper[String(cell.id).trim().toUpperCase()];
            return sa != null ? { ...cell, solarAccess: sa } : cell;
        }),
    }));
}

function applySimulatedMetrics(
    candidate: RankedCandidate,
    sim: PciSimulatedOptionResult | undefined,
    form: PreContractIntakeForm,
): RankedCandidate {
    if (!sim) return candidate;
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const layouts = applyLiveMetricsByModuleId(
        candidate.layouts,
        sim.solarAccessByModuleId,
        sim.productionByModuleId,
        sim.multiplierByModuleId,
    );
    const saFromMeshes = Object.values(sim.solarAccessByModuleId ?? {});
    const meshSaAvg =
        saFromMeshes.length > 0
            ? saFromMeshes.reduce((sum, value) => sum + value, 0) / saFromMeshes.length
            : 0;
    const moduleProductionSum = sumIncludedModuleProductionKwh(
        layouts,
        candidate.roofSelections,
    );
    const moduleAnnualSum = sumIncludedModuleAnnualKwh(
        layouts,
        candidate.roofSelections,
        form,
    );
    const annualKwh =
        moduleAnnualSum > 0
            ? moduleAnnualSum
            : moduleProductionSum > 0
              ? moduleProductionSum
              : candidate.annualKwh > 0
                ? candidate.annualKwh
                : 0;
    const next: RankedCandidate = {
        ...candidate,
        layouts,
        annualKwh,
        utilityKwh: sim.utilityKwh > 0 ? sim.utilityKwh : candidate.utilityKwh ?? null,
        averageSolarAccess:
            sim.averageSolarAccess > 0
                ? sim.averageSolarAccess
                : meshSaAvg > 0
                  ? meshSaAvg
                  : candidate.averageSolarAccess,
        efficiencyTsrf:
            sim.efficiency != null && sim.efficiency > 0
                ? sim.efficiency
                : candidate.efficiencyTsrf ?? null,
        multiplier:
            sim.multiplier != null && sim.multiplier > 0
                ? sim.multiplier
                : candidate.multiplier ?? null,
        moduleCount: sim.moduleCount > 0 ? sim.moduleCount : candidate.moduleCount,
        systemKw: sim.systemKw > 0 ? sim.systemKw : candidate.systemKw,
        offsetPercent: usageKwh > 0 ? Math.round((annualKwh / usageKwh) * 100) : null,
        // Prefer the simulated included placement over the full max-fill snapshot.
        modulesLayout: sim.modulesLayout?.length
            ? sim.modulesLayout
            : candidate.modulesLayout,
    };
    return { ...next, score: scoreCandidate(next, form) };
}

async function simulateCandidatesInEmbed(
    candidates: RankedCandidate[],
    form: PreContractIntakeForm,
    fills?: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>>,
    ghostedTreeIds?: string[],
): Promise<RankedCandidate[]> {
    const jobs = candidates.map((candidate) => ({
        id: candidate.id,
        includedModuleIds: collectIncludedModuleIds(candidate.layouts),
        fillOrientation: candidate.fillOrientation,
    }));
    const started = Date.now();
    const results = await requestPciSimulateOptionsFromEmbed(jobs, fills, ghostedTreeIds);
    if (results?.length) {
        const byId = new Map(results.map((row) => [row.id, row]));
        const next = candidates.map((candidate) =>
            applySimulatedMetrics(candidate, byId.get(candidate.id), form),
        );
        next.sort((a, b) => b.score - a.score);
        return next;
    }
    if (Date.now() - started > 20000) return candidates;
    await new Promise((resolve) => window.setTimeout(resolve, 1200));
    const retry = await requestPciSimulateOptionsFromEmbed(jobs, fills, ghostedTreeIds);
    if (!retry?.length) return candidates;
    const byId = new Map(retry.map((row) => [row.id, row]));
    const next = candidates.map((candidate) =>
        applySimulatedMetrics(candidate, byId.get(candidate.id), form),
    );
    next.sort((a, b) => b.score - a.score);
    return next;
}

function layoutSignature(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    fillOrientation?: PreContractFillOrientation,
): string {
    const ids: string[] = [];
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) ids.push(cell.id);
        });
    });
    ids.sort();
    return `${fillOrientation ?? ''}|${ids.join('|')}`;
}

function systemKwFromCount(moduleCount: number, wattage: number | null): number {
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0 || moduleCount <= 0) return 0;
    return Math.round(((moduleCount * wattage) / 1000) * 1000) / 1000;
}

/** Average production multiplier across included modules (Production panel Multiplier). */
function averageIncludedMultiplier(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number | null {
    let sum = 0;
    let count = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const multiplier = Number(cell.multiplier);
            if (!Number.isFinite(multiplier) || multiplier <= 0) return;
            sum += multiplier;
            count += 1;
        });
    });
    if (count === 0) return null;
    return Math.round((sum / count) * 1000) / 1000;
}

function kwhPerModule(wattage: number | null): number {
    if (wattage == null || !Number.isFinite(wattage) || wattage <= 0) return 0;
    return (wattage / 1000) * SPECIFIC_YIELD;
}

function poolCells(
    layouts: PreContractRoofModuleLayout[],
    form: PreContractIntakeForm,
    roofSelections?: boolean[],
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    return collectPresentCells(layouts).filter(({ roofIndex, cell }) => {
        if (roofSelections && roofSelections[roofIndex] === false) return false;
        return isPreContractPanelAllowed(
            cell.solarAccess,
            form.includeGreyPanels,
            form.includePurplePanels,
        );
    });
}

/** @deprecated Use poolCells with form — kept as alias for callers that expect the old name. */
function eligibleCells(
    layouts: PreContractRoofModuleLayout[],
    allowGray: boolean,
    roofSelections?: boolean[],
    form?: PreContractIntakeForm,
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    if (form) return poolCells(layouts, form, roofSelections);
    const includeGrey = allowGray;
    const includePurple = allowGray;
    return collectPresentCells(layouts).filter(({ roofIndex, cell }) => {
        if (roofSelections && roofSelections[roofIndex] === false) return false;
        return isPreContractPanelAllowed(cell.solarAccess, includeGrey, includePurple);
    });
}

function buildOption(
    id: string,
    strategy: CandidateStrategy,
    title: string,
    reason: string,
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
    score: number,
): RankedCandidate | null {
    const { count, averageSolarAccess } = countIncludedModules(layouts, roofSelections);
    if (count <= 0) return null;

    const annualKwh = sumIncludedModuleAnnualKwh(layouts, roofSelections, form);
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const includesGreyPanels = layouts.some((layout) => {
        if (roofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => cell?.included && isGreyPanel(cell));
    });
    const includesPurplePanels = layouts.some((layout) => {
        if (roofSelections[layout.roofIndex] === false) return false;
        return layout.modules.some((cell) => cell?.included && isPurplePanel(cell));
    });
    const includesGrayPanels = includesGreyPanels || includesPurplePanels;
    const roofLabels = layouts
        .filter((layout) => roofSelections[layout.roofIndex] !== false)
        .filter((layout) => layout.modules.some((cell) => cell?.included))
        .map((layout) => layout.label);

    return {
        id,
        rank: 0,
        title,
        reason,
        strategy,
        score,
        layouts,
        roofSelections,
        moduleCount: count,
        systemKw: systemKwFromCount(count, form.moduleTypeWattage),
        annualKwh,
        utilityKwh: null,
        averageSolarAccess,
        efficiencyTsrf: null,
        multiplier: averageIncludedMultiplier(layouts, roofSelections),
        offsetPercent: usageKwh > 0 ? Math.round((annualKwh / usageKwh) * 100) : null,
        roofLabels,
        includesGrayPanels,
        includesGreyPanels,
        includesPurplePanels,
    };
}

function scoreCandidate(candidate: RankedCandidate, form: PreContractIntakeForm): number {
    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const offset = usageKwh > 0 ? candidate.annualKwh / usageKwh : 1;
    const balanced = form.offsetPreference === 'balanced';
    let offsetScore: number;

    if (usageKwh > 0) {
        if (offset >= 1 && offset <= MAX_UTILITY_OFFSET_RATIO) {
            offsetScore = 1;
        } else if (offset > MAX_UTILITY_OFFSET_RATIO) {
            offsetScore = Math.max(0, 1 - (offset - MAX_UTILITY_OFFSET_RATIO) * 4);
        } else {
            // Prefer meeting usage over stopping slightly under target.
            offsetScore = Math.max(0, offset) * 0.75;
        }
        if (balanced && offset < 0.7) offsetScore -= 0.18;
    } else {
        offsetScore = 1;
    }

    const saScore = Math.max(0, Math.min(1, candidate.averageSolarAccess / 100));
    let shadedPenalty = 0;
    if (!form.includeGreyPanels && candidate.includesGreyPanels) shadedPenalty += 0.18;
    if (!form.includePurplePanels && candidate.includesPurplePanels) shadedPenalty += 0.22;
    if (form.includeGreyPanels && !candidate.includesGreyPanels && offset < 0.85) {
        shadedPenalty += 0.04;
    }
    if (form.includePurplePanels && !candidate.includesPurplePanels && offset < 0.85) {
        shadedPenalty += 0.04;
    }
    if (balanced && candidate.includesGrayPanels && offset >= 0.98) shadedPenalty += 0.12;

    const includedIds = includedIdsFromLayouts(candidate.layouts, candidate.roofSelections);
    const adjacencyScore = layoutAdjacencyScore(
        includedIds,
        buildModuleGridContext(candidate.layouts, candidate.roofSelections),
    );
    const adjacencyBonus = Math.max(0, Math.min(0.08, adjacencyScore * 0.02));

    const offsetWeight = balanced ? 0.5 : 0.55;
    const saWeight = balanced ? 0.4 : 0.33;
    return offsetScore * offsetWeight + saScore * saWeight - shadedPenalty + adjacencyBonus;
}

function defaultCopy(
    strategy: CandidateStrategy,
    form: PreContractIntakeForm,
): { title: string; reason: string } {
    const full = form.offsetPreference === 'full';
    const skipGrey = !form.includeGreyPanels;
    const skipPurple = !form.includePurplePanels;
    switch (strategy) {
        case 'your-layout':
            return {
                title: 'Your current layout',
                reason: 'Keeps the roofs and modules you already included on the previous screen.',
            };
        case 'max-production':
            return {
                title: full ? 'Full-offset max production' : 'Highest production',
                reason:
                    skipGrey && skipPurple
                        ? 'Fills eligible roofs while skipping grey and purple panels.'
                        : skipGrey
                          ? 'Fills eligible roofs while skipping grey (30–50% SA) panels.'
                          : skipPurple
                            ? 'Fills eligible roofs while skipping purple (<30% SA) panels.'
                            : 'Places as many modules as possible to maximize annual production.',
            };
        case 'usage-fit':
            return {
                title: full ? 'Target utility usage' : 'Balanced usage fit',
                reason:
                    'Adds the highest-production modules in contiguous roof blocks until annual utility usage is covered, stopping before exceeding 110% of usage.',
            };
        case 'high-access':
            return {
                title: 'Best solar access',
                reason: 'Keeps only high solar-access modules so the array stays efficient and cleaner.',
            };
        case 'best-roofs':
            return {
                title: 'Best roofs only',
                reason: 'Concentrates modules on the strongest roof faces instead of spreading across the whole house.',
            };
        default:
            return { title: 'Design option', reason: 'Generated from the intake you provided.' };
    }
}

function asStrategy(id: string): CandidateStrategy {
    const key = id.replace(/^(portrait|landscape|both)-/, '');
    if (
        key === 'your-layout' ||
        key === 'max-production' ||
        key === 'usage-fit' ||
        key === 'high-access' ||
        key === 'best-roofs'
    ) {
        return key;
    }
    return 'usage-fit';
}

function asFillOrientation(value: unknown): PreContractFillOrientation | undefined {
    if (value === 'portrait' || value === 'landscape' || value === 'both') return value;
    const text = String(value ?? '').toLowerCase();
    if (text.includes('land')) return 'landscape';
    if (text.includes('mix') || text.includes('both')) return 'both';
    if (text.includes('port')) return 'portrait';
    return undefined;
}

function candidateFromIds(
    id: string,
    includedIds: Set<string>,
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate | null {
    const strategyKey = id.replace(/^(portrait|landscape|both)-/, '');
    const strategy = asStrategy(strategyKey);
    const { layouts, roofSelections } = withIncludedIds(
        form.roofModuleLayouts,
        includedIds,
        form.roofSelections,
    );
    const copy = defaultCopy(strategy, form);
    const optionId = fillOrientation ? `${fillOrientation}-${strategyKey}` : id;
    const draft = buildOption(
        optionId,
        strategy,
        copy.title,
        copy.reason,
        layouts,
        roofSelections,
        form,
        0,
    );
    if (!draft) return null;
    const withOrientation: RankedCandidate = { ...draft, fillOrientation };
    return { ...withOrientation, score: scoreCandidate(withOrientation, form) };
}

function idsForYourLayout(form: PreContractIntakeForm): Set<string> {
    const ids = new Set<string>();
    form.roofModuleLayouts.forEach((layout) => {
        if (form.roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) ids.add(cell.id);
        });
    });
    return ids;
}

function idsForMaxProduction(form: PreContractIntakeForm): Set<string> {
    const ids = new Set<string>();
    poolCells(form.roofModuleLayouts, form, form.roofSelections).forEach(({ cell }) => ids.add(cell.id));
    return ids;
}

function moduleAnnualKwh(
    cell: NonNullable<PreContractModuleCell>,
    wattage: number | null,
): number {
    if (typeof cell.productionKwh === 'number' && cell.productionKwh > 0) {
        return cell.productionKwh;
    }
    const base = kwhPerModule(wattage);
    if (base <= 0) return 0;
    const sa =
        Number.isFinite(cell.solarAccess) && cell.solarAccess > 0
            ? Math.min(cell.solarAccess, 100) / 100
            : 1;
    return base * sa;
}

function moduleTargetMultiplier(cell: NonNullable<PreContractModuleCell>): number {
    const multiplier = Number(cell.multiplier);
    if (Number.isFinite(multiplier) && multiplier > 0) return multiplier;
    const sa = Number.isFinite(cell.solarAccess) && cell.solarAccess > 0 ? cell.solarAccess : 0;
    return sa / 100;
}

function compareModulesForTargetSelection(
    a: { cell: NonNullable<PreContractModuleCell> },
    b: { cell: NonNullable<PreContractModuleCell> },
    wattage: number | null,
): number {
    const saDelta = b.cell.solarAccess - a.cell.solarAccess;
    if (saDelta !== 0) return saDelta;
    const multDelta = moduleTargetMultiplier(b.cell) - moduleTargetMultiplier(a.cell);
    if (multDelta !== 0) return multDelta;
    return moduleAnnualKwh(b.cell, wattage) - moduleAnnualKwh(a.cell, wattage);
}

/** Greedy usage-fit: highest production first, then multiplier, then SA. */
function compareModulesForProductionFill(
    a: { cell: NonNullable<PreContractModuleCell> },
    b: { cell: NonNullable<PreContractModuleCell> },
    wattage: number | null,
): number {
    const prodDelta = moduleAnnualKwh(b.cell, wattage) - moduleAnnualKwh(a.cell, wattage);
    if (prodDelta !== 0) return prodDelta;
    const multDelta = moduleTargetMultiplier(b.cell) - moduleTargetMultiplier(a.cell);
    if (multDelta !== 0) return multDelta;
    return b.cell.solarAccess - a.cell.solarAccess;
}

function sortedPoolCells(
    form: PreContractIntakeForm,
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    return poolCells(form.roofModuleLayouts, form, form.roofSelections).sort((a, b) =>
        compareModulesForTargetSelection(a, b, form.moduleTypeWattage),
    );
}

function sortedPoolCellsByProduction(
    form: PreContractIntakeForm,
): Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> {
    return poolCells(form.roofModuleLayouts, form, form.roofSelections).sort((a, b) =>
        compareModulesForProductionFill(a, b, form.moduleTypeWattage),
    );
}

type ModuleGridSlot = {
    roofIndex: number;
    gridIndex: number;
    row: number;
    col: number;
    cell: NonNullable<PreContractModuleCell>;
    neighborIds: string[];
};

type ModuleGridContext = {
    byId: Map<string, ModuleGridSlot>;
    byRoof: Map<number, ModuleGridSlot[]>;
    layoutsByRoof: Map<number, PreContractRoofModuleLayout>;
};

function buildModuleGridContext(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): ModuleGridContext {
    const byId = new Map<string, ModuleGridSlot>();
    const byRoof = new Map<number, ModuleGridSlot[]>();
    const layoutsByRoof = new Map<number, PreContractRoofModuleLayout>();

    layouts.forEach((layout) => {
        if (roofSelections && roofSelections[layout.roofIndex] === false) return;
        layoutsByRoof.set(layout.roofIndex, layout);
        const cols = Math.max(1, layout.cols);
        const slots: ModuleGridSlot[] = [];

        layout.modules.forEach((cell, gridIndex) => {
            if (!cell) return;
            slots.push({
                roofIndex: layout.roofIndex,
                gridIndex,
                row: Math.floor(gridIndex / cols),
                col: gridIndex % cols,
                cell,
                neighborIds: [],
            });
        });

        slots.forEach((slot) => {
            const deltas: Array<[number, number]> = [
                [-1, 0],
                [1, 0],
                [0, -1],
                [0, 1],
            ];
            deltas.forEach(([dr, dc]) => {
                const neighborIndex = (slot.row + dr) * cols + (slot.col + dc);
                const neighbor = layout.modules[neighborIndex];
                if (neighbor?.id) slot.neighborIds.push(neighbor.id);
            });
        });

        byRoof.set(layout.roofIndex, slots);
        slots.forEach((slot) => byId.set(slot.cell.id, slot));
    });

    return { byId, byRoof, layoutsByRoof };
}

function includedIdsFromLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): Set<string> {
    const ids = new Set<string>();
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) ids.add(cell.id);
        });
    });
    return ids;
}

function isAdjacentToSelected(
    moduleId: string,
    selected: Set<string>,
    ctx: ModuleGridContext,
): boolean {
    const slot = ctx.byId.get(moduleId);
    if (!slot || selected.size === 0) return false;
    return slot.neighborIds.some((neighborId) => selected.has(neighborId));
}

function countSelectedNeighbors(
    moduleId: string,
    selected: Set<string>,
    ctx: ModuleGridContext,
): number {
    const slot = ctx.byId.get(moduleId);
    if (!slot) return 0;
    return slot.neighborIds.filter((neighborId) => selected.has(neighborId)).length;
}

function connectedComponentsOnRoof(
    moduleIds: Set<string>,
    slots: ModuleGridSlot[],
): number {
    const onRoof = slots.filter((slot) => moduleIds.has(slot.cell.id));
    if (onRoof.length <= 1) return onRoof.length;

    const visited = new Set<string>();
    let components = 0;

    const walk = (startId: string) => {
        const stack = [startId];
        visited.add(startId);
        while (stack.length > 0) {
            const id = stack.pop();
            if (!id) continue;
            const slot = onRoof.find((row) => row.cell.id === id);
            if (!slot) continue;
            slot.neighborIds.forEach((neighborId) => {
                if (!moduleIds.has(neighborId) || visited.has(neighborId)) return;
                visited.add(neighborId);
                stack.push(neighborId);
            });
        }
    };

    onRoof.forEach((slot) => {
        if (visited.has(slot.cell.id)) return;
        components += 1;
        walk(slot.cell.id);
    });

    return components;
}

/** Higher is better — rewards touching selected neighbors and penalizes fragmented roofs. */
function layoutAdjacencyScore(includedIds: Set<string>, ctx: ModuleGridContext): number {
    if (includedIds.size <= 1) return includedIds.size;

    let score = 0;
    ctx.byRoof.forEach((slots) => {
        const onRoof = slots.filter((slot) => includedIds.has(slot.cell.id));
        if (onRoof.length === 0) return;

        onRoof.forEach((slot) => {
            score += countSelectedNeighbors(slot.cell.id, includedIds, ctx);
        });

        const components = connectedComponentsOnRoof(includedIds, slots);
        if (components > 1) score -= (components - 1) * 8;
    });

    return score;
}

function isEligiblePoolCell(
    roofIndex: number,
    cell: NonNullable<PreContractModuleCell>,
    form: PreContractIntakeForm,
): boolean {
    if (form.roofSelections[roofIndex] === false) return false;
    return isPreContractPanelAllowed(
        cell.solarAccess,
        form.includeGreyPanels,
        form.includePurplePanels,
    );
}

/**
 * Add grid-adjacent modules (≥1 selected neighbor) while still below target.
 * Stops once usage is covered so contiguity does not balloon the array toward 110%.
 */
function fillAdjacentGapsInSelection(
    includedIds: Set<string>,
    form: PreContractIntakeForm,
    ctx: ModuleGridContext,
    maxKwh: number,
    target: number,
): Set<string> {
    let next = new Set(includedIds);
    let changed = true;

    while (changed) {
        const currentTotal = effectiveProductionFromIncludedIds(next, form);
        if (target > 0 && currentTotal >= target) break;

        changed = false;
        ctx.byRoof.forEach((slots, roofIndex) => {
            for (const slot of slots) {
                if (next.has(slot.cell.id)) continue;
                if (!isEligiblePoolCell(roofIndex, slot.cell, form)) continue;

                const selectedNeighbors = countSelectedNeighbors(slot.cell.id, next, ctx);
                // Contiguous = shares an edge with at least one selected module on this roof.
                if (selectedNeighbors < 1) continue;

                const trial = new Set(next);
                trial.add(slot.cell.id);
                const total = effectiveProductionFromIncludedIds(trial, form);
                if (total <= 0 || total > maxKwh) continue;

                next = trial;
                changed = true;
            }
        });
    }

    return next;
}

function trimExcessModulesWhileMeetingTarget(
    includedIds: Set<string>,
    form: PreContractIntakeForm,
    target: number,
    maxKwh: number,
    ctx: ModuleGridContext,
): Set<string> {
    let next = new Set(includedIds);
    let changed = true;

    while (changed) {
        changed = false;
        const total = effectiveProductionFromIncludedIds(next, form);
        if (total < target) break;

        const removalOrder = [...next].sort((leftId, rightId) => {
            const leftNeighbors = countSelectedNeighbors(leftId, next, ctx);
            const rightNeighbors = countSelectedNeighbors(rightId, next, ctx);
            if (leftNeighbors !== rightNeighbors) return leftNeighbors - rightNeighbors;
            const leftSlot = ctx.byId.get(leftId);
            const rightSlot = ctx.byId.get(rightId);
            const leftProd = leftSlot
                ? moduleAnnualKwh(leftSlot.cell, form.moduleTypeWattage)
                : 0;
            const rightProd = rightSlot
                ? moduleAnnualKwh(rightSlot.cell, form.moduleTypeWattage)
                : 0;
            return leftProd - rightProd;
        });

        for (const moduleId of removalOrder) {
            const trial = new Set(next);
            trial.delete(moduleId);
            const trialTotal = effectiveProductionFromIncludedIds(trial, form);
            if (trialTotal >= target && trialTotal <= maxKwh) {
                next = trial;
                changed = true;
                break;
            }
        }
    }

    return next;
}

function pickBestProductionCandidate(
    candidates: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    form: PreContractIntakeForm,
): { roofIndex: number; cell: NonNullable<PreContractModuleCell> } | null {
    if (candidates.length === 0) return null;
    return [...candidates].sort((a, b) =>
        compareModulesForProductionFill(a, b, form.moduleTypeWattage),
    )[0];
}

function pickInitialAdjacentSeed(
    cells: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    form: PreContractIntakeForm,
    ctx: ModuleGridContext,
): Set<string> {
    const poolIds = new Set(cells.map(({ cell }) => cell.id));
    let bestIds: Set<string> | null = null;
    let bestTotal = 0;

    ctx.byRoof.forEach((slots) => {
        slots.forEach((slot) => {
            if (!poolIds.has(slot.cell.id)) return;
            slot.neighborIds.forEach((neighborId) => {
                if (!poolIds.has(neighborId) || slot.cell.id >= neighborId) return;
                const trial = new Set([slot.cell.id, neighborId]);
                const total = effectiveProductionFromIncludedIds(trial, form);
                if (total > bestTotal) {
                    bestTotal = total;
                    bestIds = trial;
                }
            });
        });
    });

    if (bestIds) return bestIds;

    const seed = pickBestProductionCandidate(cells, form);
    return seed ? new Set([seed.cell.id]) : new Set();
}

function growSelectionTowardTarget(
    selected: Set<string>,
    cells: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    form: PreContractIntakeForm,
    ctx: ModuleGridContext,
    target: number,
    maxKwh: number,
): Set<string> {
    const next = new Set(selected);

    while (effectiveProductionFromIncludedIds(next, form) < target) {
        const remaining = cells.filter(({ cell }) => !next.has(cell.id));
        if (remaining.length === 0) break;

        const adjacent = remaining.filter(({ cell }) => isAdjacentToSelected(cell.id, next, ctx));
        const pool = adjacent.length > 0 ? adjacent : remaining;
        const feasible = pool.filter(({ cell }) => {
            const trial = new Set(next);
            trial.add(cell.id);
            return effectiveProductionFromIncludedIds(trial, form) <= maxKwh;
        });
        const pick = pickBestProductionCandidate(feasible, form);
        if (!pick) break;
        next.add(pick.cell.id);
    }

    return next;
}

function pickBetterTargetSelection(
    left: Set<string>,
    right: Set<string>,
    form: PreContractIntakeForm,
    ctx: ModuleGridContext,
    target: number,
): Set<string> {
    const leftTotal = effectiveProductionFromIncludedIds(left, form);
    const rightTotal = effectiveProductionFromIncludedIds(right, form);
    const leftMeets = leftTotal >= target;
    const rightMeets = rightTotal >= target;

    if (leftMeets && !rightMeets) return left;
    if (rightMeets && !leftMeets) return right;

    if (leftMeets && rightMeets) {
        if (leftTotal !== rightTotal) return leftTotal <= rightTotal ? left : right;
        const leftAdj = layoutAdjacencyScore(left, ctx);
        const rightAdj = layoutAdjacencyScore(right, ctx);
        return leftAdj >= rightAdj ? left : right;
    }

    return leftTotal >= rightTotal ? left : right;
}

/** Prefix scan fallback — keeps prior meet-target behavior when growth stalls. */
function idsFromGreedyPrefixScan(
    form: PreContractIntakeForm,
    cells: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    target: number,
): Set<string> {
    const maxKwh = maxAllowedProductionKwh(target);
    const prefixIds: string[] = [];
    let bestMeetingTarget: { ids: string[]; total: number; adjacency: number } | null = null;
    let bestUnderCap: { ids: string[]; total: number; adjacency: number } | null = null;
    const ctx = buildModuleGridContext(form.roofModuleLayouts, form.roofSelections);

    for (const { cell } of cells) {
        if (moduleAnnualKwh(cell, form.moduleTypeWattage) <= 0) continue;

        prefixIds.push(cell.id);
        const idSet = new Set(prefixIds);
        const total = effectiveProductionFromIncludedIds(idSet, form);
        if (total <= 0 || total > maxKwh) continue;

        const adjacency = layoutAdjacencyScore(idSet, ctx);
        const snapshot = { ids: [...prefixIds], total, adjacency };

        if (total >= target) {
            if (
                !bestMeetingTarget ||
                snapshot.total < bestMeetingTarget.total ||
                (snapshot.total === bestMeetingTarget.total &&
                    snapshot.adjacency > bestMeetingTarget.adjacency)
            ) {
                bestMeetingTarget = snapshot;
            }
        }

        if (
            !bestUnderCap ||
            snapshot.total > bestUnderCap.total ||
            (snapshot.total === bestUnderCap.total && snapshot.adjacency > bestUnderCap.adjacency)
        ) {
            bestUnderCap = snapshot;
        }
    }

    if (bestMeetingTarget) return new Set(bestMeetingTarget.ids);
    if (bestUnderCap) return new Set(bestUnderCap.ids);
    return new Set();
}

function selectIdsForTargetUsageWithAdjacency(
    form: PreContractIntakeForm,
    cells: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    target: number,
): Set<string> {
    if (cells.length === 0) return new Set();
    if (target <= 0) {
        return new Set(cells.map(({ cell }) => cell.id));
    }

    const maxKwh = maxAllowedProductionKwh(target);
    const ctx = buildModuleGridContext(form.roofModuleLayouts, form.roofSelections);

    let selected = pickInitialAdjacentSeed(cells, form, ctx);

    selected = growSelectionTowardTarget(selected, cells, form, ctx, target, maxKwh);
    selected = fillAdjacentGapsInSelection(selected, form, ctx, maxKwh, target);
    selected = growSelectionTowardTarget(selected, cells, form, ctx, target, maxKwh);
    selected = trimExcessModulesWhileMeetingTarget(selected, form, target, maxKwh, ctx);

    const prefix = idsFromGreedyPrefixScan(form, cells, target);
    selected = pickBetterTargetSelection(selected, prefix, form, ctx, target);
    selected = trimExcessModulesWhileMeetingTarget(selected, form, target, maxKwh, ctx);

    if (effectiveProductionFromIncludedIds(selected, form) > maxKwh) {
        selected = trimExcessModulesWhileMeetingTarget(selected, form, target, maxKwh, ctx);
    }

    return selected;
}

/** Production after AI roof rules (≥2 modules per active roof). */
function effectiveProductionFromIncludedIds(
    includedIds: Set<string>,
    form: PreContractIntakeForm,
): number {
    const { layouts, roofSelections } = withIncludedIds(
        form.roofModuleLayouts,
        includedIds,
        form.roofSelections,
    );
    return sumIncludedModuleAnnualKwh(layouts, roofSelections, form);
}

/**
 * Pick modules to reach annual usage without exceeding 110%.
 * Grows contiguous roof blocks when possible, fills inline gaps, then falls back to prefix scan.
 */
function idsFromGreedyTargetFill(
    form: PreContractIntakeForm,
    cells: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }>,
    target: number,
): Set<string> {
    return selectIdsForTargetUsageWithAdjacency(form, cells, target);
}

/** Included module ids in greedy fill order (best SA / multiplier first). */
export function orderIncludedModuleIdsForFill(
    form: PreContractIntakeForm,
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): string[] {
    const selections = roofSelections ?? form.roofSelections;
    const included = new Set<string>();
    layouts.forEach((layout) => {
        if (selections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included) included.add(cell.id);
        });
    });
    return sortedPoolCellsByProduction({ ...form, roofModuleLayouts: layouts, roofSelections: selections })
        .filter(({ cell }) => included.has(cell.id))
        .map(({ cell }) => cell.id);
}

/** Size the array to annual utility usage (annualUsageKwh). */
function idsForTargetUsage(form: PreContractIntakeForm): Set<string> {
    const target = parseUsageKwh(form.annualUsageKwh);
    return idsFromGreedyTargetFill(form, sortedPoolCellsByProduction(form), target);
}

function sumIncludedModuleAnnualKwh(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
): number {
    let kwh = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            kwh += moduleAnnualKwh(cell, form.moduleTypeWattage);
        });
    });
    if (kwh > 0) return Math.round(kwh);
    return (
        estimateLayoutAnnualKwh({
            layouts,
            roofSelections,
            moduleWattage: form.moduleTypeWattage,
        }) ?? 0
    );
}

function idsForUsageFit(form: PreContractIntakeForm): Set<string> {
    return idsForTargetUsage(form);
}

function idsForHighAccess(form: PreContractIntakeForm): Set<string> {
    const cells = poolCells(form.roofModuleLayouts, form, form.roofSelections);
    const strict = cells.filter(({ cell }) => cell.solarAccess >= HIGH_ACCESS_SA);
    const pool = strict.length >= 4 ? strict : cells.filter(({ cell }) => cell.solarAccess >= RELAXED_ACCESS_SA);
    const chosen = pool.length > 0 ? pool : cells;
    return new Set(chosen.map(({ cell }) => cell.id));
}

function idsForBestRoofs(form: PreContractIntakeForm): Set<string> {
    const cells = poolCells(form.roofModuleLayouts, form, form.roofSelections);
    if (cells.length === 0) return new Set();

    const byRoof = new Map<number, Array<NonNullable<PreContractModuleCell>>>();
    cells.forEach(({ roofIndex, cell }) => {
        const list = byRoof.get(roofIndex) ?? [];
        list.push(cell);
        byRoof.set(roofIndex, list);
    });

    const rankedRoofs = [...byRoof.entries()]
        .map(([roofIndex, roofCells]) => {
            const avg = roofCells.reduce((sum, cell) => sum + cell.solarAccess, 0) / roofCells.length;
            return { roofIndex, avg, roofCells };
        })
        .sort((a, b) => b.avg - a.avg);

    const ordered: Array<{ roofIndex: number; cell: NonNullable<PreContractModuleCell> }> = [];
    for (const roof of rankedRoofs) {
        const roofCells = [...roof.roofCells].sort((left, right) =>
            compareModulesForTargetSelection(
                { cell: left },
                { cell: right },
                form.moduleTypeWattage,
            ),
        );
        roofCells.forEach((cell) => ordered.push({ roofIndex: roof.roofIndex, cell }));
    }

    const target = parseUsageKwh(form.annualUsageKwh);
    return idsFromGreedyTargetFill(form, ordered, target);
}

function collectHeuristicCandidates(
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate[] {
    const drafts: Array<RankedCandidate | null> = [
        candidateFromIds('your-layout', idsForYourLayout(form), form, fillOrientation),
        candidateFromIds('max-production', idsForMaxProduction(form), form, fillOrientation),
        candidateFromIds('usage-fit', idsForUsageFit(form), form, fillOrientation),
        candidateFromIds('high-access', idsForHighAccess(form), form, fillOrientation),
        candidateFromIds('best-roofs', idsForBestRoofs(form), form, fillOrientation),
    ];

    const seen = new Set<string>();
    const unique: RankedCandidate[] = [];
    drafts.forEach((draft) => {
        if (!draft) return;
        const sig = layoutSignature(draft.layouts, draft.roofSelections, draft.fillOrientation);
        if (!sig || seen.has(sig)) return;
        seen.add(sig);
        unique.push(draft);
    });

    unique.sort((a, b) => b.score - a.score);
    return unique;
}

function parseGeminiPicks(raw: string): GeminiPick[] {
    const trimmed = raw.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
    try {
        const parsed = JSON.parse(trimmed) as { layouts?: GeminiPick[]; picks?: GeminiPick[] } | GeminiPick[];
        const picks = Array.isArray(parsed) ? parsed : parsed.layouts ?? parsed.picks;
        if (!Array.isArray(picks)) return [];
        return picks
            .filter((pick) => pick && typeof pick.id === 'string' && pick.id.trim())
            .map((pick) => ({
                ...pick,
                orientation: asFillOrientation(pick.orientation ?? (pick as { strategy?: string }).strategy),
            }));
    } catch {
        return [];
    }
}

function cellMatchKeys(cell: { id?: string; meshId?: string }): string[] {
    const keys: string[] = [];
    const id = String(cell.id ?? '').trim().toUpperCase();
    if (id) keys.push(id);
    const meshId = String(cell.meshId ?? '').trim().toUpperCase();
    if (meshId) keys.push(meshId);
    return keys;
}

function knownModuleIdMap(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): Map<string, string> {
    const known = new Map<string, string>();
    layouts.forEach((layout) => {
        if (roofSelections && roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.id || !cell.included) return;
            cellMatchKeys(cell).forEach((key) => known.set(key, cell.id));
        });
    });
    return known;
}

function resolveModuleIds(
    rawIds: string[] | undefined,
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): Set<string> {
    const known = knownModuleIdMap(layouts, roofSelections);
    const ids = new Set<string>();
    (rawIds ?? []).forEach((id) => {
        const match = known.get(String(id).trim().toUpperCase());
        if (match) ids.add(match);
    });
    return ids;
}

function intakeLayoutFor(
    form: PreContractIntakeForm,
    layout: PreContractRoofModuleLayout,
    index: number,
): PreContractRoofModuleLayout | undefined {
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        const match = form.roofModuleLayouts.find((row) => row.roofTagId === layout.roofTagId);
        if (match) return match;
    }
    return (
        form.roofModuleLayouts.find((row) => row.roofIndex === layout.roofIndex) ??
        form.roofModuleLayouts[index]
    );
}

function intakeRoofHasSelectedModules(layout: PreContractRoofModuleLayout | undefined): boolean {
    if (!layout) return true;
    if (!layout.modules.some(Boolean)) return true;
    return layout.modules.some((cell) => Boolean(cell?.included));
}

function roofIsActiveInForm(
    form: PreContractIntakeForm,
    layout: PreContractRoofModuleLayout,
    index: number,
): boolean {
    const intake = intakeLayoutFor(form, layout, index);
    if (!intakeRoofHasSelectedModules(intake)) return false;
    if (intake && typeof form.roofSelections[intake.roofIndex] === 'boolean') {
        return form.roofSelections[intake.roofIndex] !== false;
    }
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        const match = form.roofModuleLayouts.find((row) => row.roofTagId === layout.roofTagId);
        if (match) return form.roofSelections[match.roofIndex] !== false;
    }
    if (typeof form.roofSelections[layout.roofIndex] === 'boolean') {
        return form.roofSelections[layout.roofIndex] !== false;
    }
    return form.roofSelections[index] !== false;
}

function alignRoofSelections(
    layouts: PreContractRoofModuleLayout[],
    form: PreContractIntakeForm,
): boolean[] {
    return layouts.map((layout, index) => roofIsActiveInForm(form, layout, index));
}

function applyMaxfillProduction(
    layouts: PreContractRoofModuleLayout[],
    maxfill: PciMaxfillSnapshot,
): PreContractRoofModuleLayout[] {
    const byId = new Map<string, PciMaxfillSnapshot['modules'][number]>();
    maxfill.modules.forEach((row) => {
        byId.set(String(row.id).trim().toUpperCase(), row);
    });
    return layouts.map((layout) => ({
        ...layout,
        modules: layout.modules.map((cell) => {
            if (!cell) return null;
            const row = byId.get(String(cell.id).trim().toUpperCase());
            if (!row) return cell;
            return {
                ...cell,
                solarAccess: row.solarAccess > 0 ? row.solarAccess : cell.solarAccess,
                ...(row.multiplier > 0 ? { multiplier: row.multiplier } : {}),
                ...(row.productionKwh > 0 ? { productionKwh: row.productionKwh } : {}),
            };
        }),
    }));
}

async function designLayoutsWithGemini(
    form: PreContractIntakeForm,
    snapshots: PciMaxfillSnapshot[],
): Promise<GeminiPick[] | null> {
    const apiKey = import.meta.env?.VITE_GEMINI_API_KEY as string | undefined;
    const usable = snapshots.filter((row) => row.modules.length > 0);
    if (!apiKey || usable.length === 0) return null;

    const usageKwh = parseUsageKwh(form.annualUsageKwh);
    const targetKwh = usageKwh;
    const maxProductionKwh = maxAllowedProductionKwh(usageKwh);
    const payload = {
        targetKwh: targetKwh || null,
        maxProductionKwh: Number.isFinite(maxProductionKwh) ? maxProductionKwh : null,
        maxOffsetPercent: Math.round(MAX_UTILITY_OFFSET_RATIO * 100),
        annualUsageKwh: usageKwh || null,
        mustCoverUsage: form.offsetPreference === 'full',
        offsetPreference: form.offsetPreference,
        grayPanelsRequired: form.includeGreyPanels && form.includePurplePanels ? 'yes' : 'no',
        includeGreyPanels: form.includeGreyPanels,
        includePurplePanels: form.includePurplePanels,
        fillOrientations: normalizeFillOrientations(form.fillOrientations),
        moduleTypeName: form.moduleTypeName || null,
        moduleWattage: form.moduleTypeWattage,
        strategies: usable.map((maxfill) => ({
            orientation: maxfill.orientation ?? 'portrait',
            annualKwh: maxfill.annualKwh,
            averageSolarAccess: maxfill.averageSolarAccess,
            moduleCount: maxfill.moduleCount,
            systemKw: maxfill.systemKw,
            efficiencyTsrf: maxfill.efficiency,
            modules: maxfill.modules.map((row) => ({
                id: row.id,
                roof: row.roofLabel,
                sa: row.solarAccess,
                multiplier: Math.round(row.multiplier * 1000) / 1000,
                kwh: row.productionKwh,
            })),
        })),
    };

    const prompt = `You are a rooftop solar designer. You receive SELECTED roofs and SELECTED modules only (inactive roofs and excluded modules are omitted). Every layout MUST keep this same roof geometry and panel placement — only which of these selected modules are included may change. Each module has REAL solar access (sa 0-100), production multiplier, and estimated annual kWh.

Design up to ${MAX_OPTIONS} distinct layouts. TARGET kWh is always annualUsageKwh (100% of usage). Never aim at a reduced percentage such as 88%. HARD CAP: estimated layout kWh must NEVER exceed maxProductionKwh (110% of annualUsageKwh).
Rules:
- Use only module ids from this input. Do not invent ids. Do not move, rotate, or mix modules from another fill.
- Keep the same roofs and the same panel positions in every layout. Layouts differ only by which of those modules are included.
- Set "orientation" to this strategy's orientation ("portrait", "landscape", or "both").
- Do not place modules on inactive roofs or use excluded/disabled modules.
- Never include a roof with fewer than 2 modules.
- Prefer contiguous blocks on each roof: include modules that share an edge with at least one selected module on the same roof (both sides not required) instead of scattered gaps when production and usage rules allow.
- Prefer higher sa and higher multiplier modules first.
- Always try to reach at least annualUsageKwh (100% offset) when eligible modules allow it, but NEVER exceed maxProductionKwh (110% of usage).
- offsetPreference "full" = COVER annual usage. Keep adding modules until estimated kWh is at or above annualUsageKwh, stopping before maxProductionKwh. Modest overshoot up to 110% is OK and preferred over falling short. Include extra roofs or lower-SA modules if needed to cover usage. Only return a layout below annualUsageKwh if every eligible module is already included or the next module would exceed 110%.
- offsetPreference "balanced" = reach as CLOSE as possible to annualUsageKwh without exceeding maxProductionKwh. Use the fewest roofs and highest SA/multiplier modules; concentrate on the best roof faces; skip gray/low-SA modules unless needed to approach the target. Closest match with a clean, efficient array is the goal — not a smaller 88% goal.
- includeGreyPanels false = do not include modules with sa from ${PRE_CONTRACT_GREY_SA_MIN} to ${PRE_CONTRACT_LOW_EFFICIENCY_SA} (grey panels).
- includePurplePanels false = do not include modules with sa < ${PRE_CONTRACT_PURPLE_SA_MAX} (purple panels).
- When includeGreyPanels or includePurplePanels is true, those shaded modules are allowed if they help hit the target. For full offset, include them when higher-SA modules are not enough to cover usage. For balanced, only include them if they clearly get you closer to usage without wrecking average SA.
- Layouts must be different from each other (different included module sets).
- Estimated layout kWh is the sum of included modules' kwh (or wattage/1000*multiplier*annual if kwh is 0).

Return JSON only:
{"layouts":[{"id":"usage-fit","orientation":"portrait","title":"short name","reason":"one sentence","moduleIds":["R1M1"]}]}
Best layout first. 1-3 layouts.

Input:
${JSON.stringify(payload)}`;

    const work = callGemini(prompt, GEMINI_MODEL_ID, apiKey, { responseMimeType: 'application/json' });
    const timeout = new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), GEMINI_DESIGN_TIMEOUT_MS);
    });

    try {
        const result = await Promise.race([work, timeout]);
        if (typeof result !== 'string') return null;
        const picks = parseGeminiPicks(result);
        return picks.length > 0 ? picks.slice(0, MAX_OPTIONS) : null;
    } catch {
        return null;
    }
}

function finalizeOptions(ordered: RankedCandidate[], picks: GeminiPick[] | null): PreContractAiDesignOption[] {
    const byId = new Map(ordered.map((c) => [c.id, c]));
    const chosen: RankedCandidate[] = [];

    if (picks) {
        picks.forEach((pick) => {
            const match = byId.get(pick.id);
            if (!match || chosen.some((c) => c.id === match.id)) return;
            chosen.push({
                ...match,
                title: pick.title?.trim() || match.title,
                reason: pick.reason?.trim() || match.reason,
            });
        });
    }

    ordered.forEach((candidate) => {
        if (chosen.length >= MAX_OPTIONS) return;
        if (chosen.some((c) => c.id === candidate.id)) return;
        chosen.push(candidate);
    });

    return chosen.slice(0, MAX_OPTIONS).map((option, index) => ({
        id: option.id,
        rank: index + 1,
        title: option.title,
        reason: option.reason,
        layouts: option.layouts,
        roofSelections: option.roofSelections,
        moduleCount: option.moduleCount,
        systemKw: option.systemKw,
        annualKwh: option.annualKwh,
        utilityKwh: option.utilityKwh ?? null,
        averageSolarAccess: option.averageSolarAccess,
        efficiencyTsrf: option.efficiencyTsrf ?? null,
        multiplier: option.multiplier ?? null,
        offsetPercent: option.offsetPercent,
        roofLabels: option.roofLabels,
        includesGrayPanels: option.includesGrayPanels,
        ...(option.fillOrientation ? { fillOrientation: option.fillOrientation } : {}),
        ...(option.modulesLayout?.length ? { modulesLayout: option.modulesLayout } : {}),
    }));
}

/** Standing tree with the highest production multiplier — the Tree Cutting tab target. */
export function pickHighestMultiplierTree(
    trees: PreContractSiteTree[],
): PreContractSiteTree | null {
    const standing = trees.filter(
        (tree) => !tree.ghosted && Number(tree.multiplier) > 0,
    );
    if (standing.length === 0) return null;
    return [...standing].sort((a, b) => {
        const delta = (Number(b.multiplier) || 0) - (Number(a.multiplier) || 0);
        if (delta !== 0) return delta;
        return String(a.label || a.id).localeCompare(String(b.label || b.id));
    })[0];
}

async function estimateTreeCutCost(tree: PreContractSiteTree): Promise<number | null> {
    try {
        const token = await treeCartService.getExcelServiceToken();
        if (typeof token !== 'string' || !token) return null;
        const height = Number(tree.heightMeters);
        return getTreeCutCostWithDefaults(token, {
            userData: {
                treeId: tree.id,
                height: Number.isFinite(height) && height > 0 ? height : 0,
                trunk_radius: tree.trunkRadiusMeters,
            },
        });
    } catch {
        return null;
    }
}

function uniqueTreeIds(ids: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    ids.forEach((id) => {
        const next = String(id).trim();
        if (!next || seen.has(next)) return;
        seen.add(next);
        out.push(next);
    });
    return out;
}

function applyDefaultIncludedToLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
): PreContractRoofModuleLayout[] {
    return layouts.map((layout, index) => {
        const active = roofSelections[index] !== false && roofSelections[layout.roofIndex] !== false;
        return {
            ...layout,
            modules: layout.modules.map((cell) => {
                if (!cell) return null;
                if (!active) return { ...cell, included: false };
                return {
                    ...cell,
                    included: isPreContractPanelAllowed(
                        cell.solarAccess,
                        form.includeGreyPanels,
                        form.includePurplePanels,
                    ),
                };
            }),
        };
    });
}

function prepareSnapshotForm(
    form: PreContractIntakeForm,
    snapshot: PciMaxfillSnapshot,
): { form: PreContractIntakeForm; activeMaxfill: PciMaxfillSnapshot | null } {
    const snapshotLayouts =
        snapshot.layouts?.length && snapshot.layouts.some((layout) => layout.modules.some(Boolean))
            ? snapshot.layouts
            : null;
    const baseLayouts = snapshotLayouts ?? form.roofModuleLayouts;
    const roofSelections = alignRoofSelections(baseLayouts, form);
    let layouts: PreContractRoofModuleLayout[];
    if (snapshotLayouts) {
        // Always start from the full max-fill grid; config toggles filter via poolCells.
        layouts = applyDefaultIncludedToLayouts(snapshotLayouts, roofSelections, form);
        const layoutOrientation =
            snapshot.orientation === 'landscape' || snapshot.orientation === 'portrait'
                ? snapshot.orientation
                : undefined;
        if (layoutOrientation) {
            layouts = layouts.map((layout) => ({
                ...layout,
                moduleOrientation: layoutOrientation,
            }));
        }
    } else {
        layouts = form.roofModuleLayouts;
    }
    const layoutsWithProduction = snapshotLayouts
        ? applyMaxfillProduction(layouts, snapshot)
        : layouts;
    return {
        form: {
            ...form,
            roofModuleLayouts: layoutsWithProduction,
            roofSelections,
        },
        activeMaxfill: snapshotLayouts ? snapshot : null,
    };
}

type PreparedMaxfill = {
    snapshot: PciMaxfillSnapshot;
    form: PreContractIntakeForm;
};

function presentModuleIdSet(layouts: PreContractRoofModuleLayout[]): Set<string> {
    const ids = new Set<string>();
    collectPresentCells(layouts).forEach(({ cell }) => ids.add(cell.id));
    return ids;
}

/** Prefer the max-fill that matches the live roof/panel layout already on the previous screen. */
function pickCanonicalPrepared(
    prepared: PreparedMaxfill[],
    intakeForm: PreContractIntakeForm,
): PreparedMaxfill | null {
    if (prepared.length === 0) return null;
    if (prepared.length === 1) return prepared[0];
    const liveIds = presentModuleIdSet(intakeForm.roofModuleLayouts);
    const liveOrientation = intakeForm.roofModuleLayouts.find(
        (layout) => layout.moduleOrientation,
    )?.moduleOrientation;
    let best = prepared[0];
    let bestScore = Number.NEGATIVE_INFINITY;
    prepared.forEach((row) => {
        const ids = presentModuleIdSet(row.form.roofModuleLayouts);
        let overlap = 0;
        ids.forEach((id) => {
            if (liveIds.has(id)) overlap += 1;
        });
        const orientationBonus =
            liveOrientation && row.snapshot.orientation === liveOrientation ? 500 : 0;
        const score = overlap * 10 + orientationBonus + (row.snapshot.moduleCount || ids.size);
        if (score > bestScore) {
            bestScore = score;
            best = row;
        }
    });
    return best;
}

/** One orientation for max-fill — avoids running portrait, landscape, and both sequentially. */
function pickPrimaryFillOrientation(form: PreContractIntakeForm): PreContractFillOrientation {
    const selected = normalizeFillOrientations(form.fillOrientations);
    if (selected.length === 1) return selected[0];
    const liveOrientation = form.roofModuleLayouts.find(
        (layout) => layout.moduleOrientation,
    )?.moduleOrientation;
    if (liveOrientation && selected.includes(liveOrientation)) return liveOrientation;
    if (selected.includes('both')) return 'both';
    return selected[0];
}

/** Best single layout from intake settings (no Gemini). */
function pickSingleHeuristicCandidate(
    form: PreContractIntakeForm,
    fillOrientation?: PreContractFillOrientation,
): RankedCandidate | null {
    const candidates = collectHeuristicCandidates(form, fillOrientation);
    if (candidates.length === 0) return null;
    const pick = (strategy: CandidateStrategy) =>
        candidates.find((candidate) => candidate.strategy === strategy);
    let chosen: RankedCandidate | null;
    const targetKwh = parseUsageKwh(form.annualUsageKwh);
    if (targetKwh > 0) {
        return (
            candidateFromIds('usage-fit', idsForTargetUsage(form), form, fillOrientation) ??
            pick('usage-fit') ??
            pick('best-roofs') ??
            candidates[0]
        );
    }

    if (form.offsetPreference === 'full') {
        chosen = pick('usage-fit') ?? pick('max-production') ?? candidates[0];
    } else {
        chosen = pick('usage-fit') ?? pick('best-roofs') ?? candidates[0];
    }
    return chosen;
}

function sizingFormFromCandidate(
    candidate: RankedCandidate,
    form: PreContractIntakeForm,
): PreContractIntakeForm {
    return {
        ...form,
        roofModuleLayouts: candidate.layouts,
        roofSelections: candidate.roofSelections,
    };
}

function collectIncludedModuleIdSet(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): Set<string> {
    const ids = new Set<string>();
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (cell?.included && cell.id) ids.add(cell.id);
        });
    });
    return ids;
}

function measureLayoutsProductionKwh(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
    form: PreContractIntakeForm,
): number {
    const productionSum = sumIncludedModuleProductionKwh(layouts, roofSelections);
    if (productionSum > 0) return productionSum;
    return sumIncludedModuleAnnualKwh(layouts, roofSelections, form);
}

function isWithinUsageWindow(measured: number, target: number, maxKwh: number): boolean {
    if (target <= 0) return true;
    return measured >= target && measured <= maxKwh;
}

function moduleProductionFromForm(moduleId: string, form: PreContractIntakeForm): number {
    for (const layout of form.roofModuleLayouts) {
        for (const cell of layout.modules) {
            if (cell?.id === moduleId) {
                return moduleAnnualKwh(cell, form.moduleTypeWattage);
            }
        }
    }
    return 0;
}

function trimIncludedIdsToUsageWindow(
    includedIds: Set<string>,
    form: PreContractIntakeForm,
    target: number,
    maxKwh: number,
): Set<string> {
    const included = new Set(includedIds);
    let total = effectiveProductionFromIncludedIds(included, form);

    while (total > maxKwh && included.size > 0) {
        const ranked = [...included]
            .map((id) => ({
                id,
                production: moduleProductionFromForm(id, form),
            }))
            .sort((a, b) => a.production - b.production);

        let removed = false;
        for (const candidate of ranked) {
            const test = new Set(included);
            test.delete(candidate.id);
            const nextTotal = effectiveProductionFromIncludedIds(test, form);
            if (nextTotal <= 0) continue;
            if (target > 0 && total >= target && nextTotal < target) continue;
            included.delete(candidate.id);
            total = nextTotal;
            removed = true;
            break;
        }
        if (!removed) break;
    }

    if (target > 0 && total < target) {
        return idsForTargetUsage(form);
    }
    return included;
}

function candidateFromIncludedIds(
    candidate: RankedCandidate,
    includedIds: Set<string>,
    form: PreContractIntakeForm,
): RankedCandidate | null {
    const rebuilt =
        candidateFromIds(candidate.id, includedIds, form, candidate.fillOrientation) ?? null;
    if (!rebuilt) return null;

    const target = parseUsageKwh(form.annualUsageKwh);
    const measured = measureLayoutsProductionKwh(
        rebuilt.layouts,
        rebuilt.roofSelections,
        form,
    );
    return {
        ...rebuilt,
        annualKwh: measured,
        offsetPercent: target > 0 ? Math.round((measured / target) * 100) : rebuilt.offsetPercent,
        efficiencyTsrf: candidate.efficiencyTsrf ?? rebuilt.efficiencyTsrf ?? null,
        multiplier: candidate.multiplier ?? rebuilt.multiplier ?? null,
        averageSolarAccess:
            candidate.averageSolarAccess > 0
                ? candidate.averageSolarAccess
                : rebuilt.averageSolarAccess,
        utilityKwh: candidate.utilityKwh ?? rebuilt.utilityKwh ?? null,
        modulesLayout: candidate.modulesLayout ?? rebuilt.modulesLayout,
        score: scoreCandidate({ ...rebuilt, annualKwh: measured }, form),
    };
}

/** Re-size module selection to [target, 110% target] using live per-module production. */
function enforceCandidateUsageWindow(
    candidate: RankedCandidate,
    form: PreContractIntakeForm,
): RankedCandidate {
    const target = parseUsageKwh(form.annualUsageKwh);
    if (target <= 0) return candidate;

    const maxKwh = maxAllowedProductionKwh(target);
    const sizingForm = sizingFormFromCandidate(candidate, form);
    const measured = measureLayoutsProductionKwh(
        candidate.layouts,
        candidate.roofSelections,
        sizingForm,
    );

    if (isWithinUsageWindow(measured, target, maxKwh)) {
        if (measured === candidate.annualKwh) return candidate;
        return {
            ...candidate,
            annualKwh: measured,
            offsetPercent: Math.round((measured / target) * 100),
            score: scoreCandidate({ ...candidate, annualKwh: measured }, sizingForm),
        };
    }

    let ids = idsForTargetUsage(sizingForm);
    ids = trimIncludedIdsToUsageWindow(ids, sizingForm, target, maxKwh);
    const rebuilt = candidateFromIncludedIds(candidate, ids, sizingForm);
    return rebuilt ?? candidate;
}

/** Final safety pass on options shown in the AI popup (also used when loading cache). */
export function finalizeUsageTargetOption(
    option: PreContractAiDesignOption,
    form: PreContractIntakeForm,
): PreContractAiDesignOption {
    const target = parseUsageKwh(form.annualUsageKwh);
    if (target <= 0) return option;

    const maxKwh = maxAllowedProductionKwh(target);
    const sizingForm: PreContractIntakeForm = {
        ...form,
        roofModuleLayouts: option.layouts,
        roofSelections: option.roofSelections,
    };
    const measured = measureLayoutsProductionKwh(
        option.layouts,
        option.roofSelections,
        sizingForm,
    );

    if (isWithinUsageWindow(measured, target, maxKwh)) {
        return {
            ...option,
            annualKwh: measured,
            offsetPercent: Math.round((measured / target) * 100),
        };
    }

    let ids = idsForTargetUsage(sizingForm);
    ids = trimIncludedIdsToUsageWindow(ids, sizingForm, target, maxKwh);
    const { layouts, roofSelections } = withIncludedIds(
        sizingForm.roofModuleLayouts,
        ids,
        sizingForm.roofSelections,
    );
    const { count, averageSolarAccess } = countIncludedModules(layouts, roofSelections);
    const annualKwh = measureLayoutsProductionKwh(layouts, roofSelections, sizingForm);
    const roofLabels = layouts
        .filter((layout) => roofSelections[layout.roofIndex] !== false)
        .filter((layout) => layout.modules.some((cell) => cell?.included))
        .map((layout) => layout.label);

    return {
        ...option,
        layouts,
        roofSelections,
        moduleCount: count,
        systemKw: systemKwFromCount(count, form.moduleTypeWattage),
        annualKwh,
        offsetPercent: Math.round((annualKwh / target) * 100),
        averageSolarAccess,
        roofLabels,
    };
}

export function sanitizePreContractAiDesignOptionsForUsageTarget(
    options: PreContractAiDesignOption[],
    form: PreContractIntakeForm,
): PreContractAiDesignOption[] {
    const target = parseUsageKwh(form.annualUsageKwh);
    if (target <= 0) return options;
    return options.map((option) => finalizeUsageTargetOption(option, form));
}

/** Generate one recommended design from the current intake configuration. */
export async function generatePreContractAiDesigns(
    form: PreContractIntakeForm,
    options?: { onProgress?: (stage: PreContractAiProgress) => void },
): Promise<PreContractAiDesignsResult> {
    const empty: PreContractAiDesignsResult = { layoutOptions: [], treeCutOptions: [] };
    const baselineGhostedIds = uniqueTreeIds(
        form.siteTrees.filter((tree) => tree.ghosted).map((tree) => tree.id),
    );
    const fillOrientations = normalizeFillOrientations(form.fillOrientations);

    let workingForm = form;
    let modulesLayout: SavedRoofModulesLayout | undefined;
    const fills: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>> = {};

    // Max-fill every orientation selected in the design config bar (Port/Land).
    options?.onProgress?.('maxfill');
    const snapshots = await requestPciMaxfillStrategiesFromEmbed(fillOrientations);
    const prepared: PreparedMaxfill[] = snapshots
        .filter(
            (row) =>
                row.layouts?.some((layout) => layout.modules.some(Boolean)) || row.modules.length > 0,
        )
        .map((snapshot) => {
            const orientation =
                snapshot.orientation ??
                (fillOrientations.length === 1 ? fillOrientations[0] : 'portrait');
            const nextSnapshot = { ...snapshot, orientation };
            return {
                snapshot: nextSnapshot,
                form: prepareSnapshotForm(form, nextSnapshot).form,
            };
        });

    const canonical = pickCanonicalPrepared(prepared, form);
    if (canonical) {
        workingForm = canonical.form;
        modulesLayout = canonical.snapshot.modulesLayout;
        persistPreContractFullModuleLayouts(
            canonical.snapshot.layouts?.some((layout) => layout.modules.some(Boolean))
                ? canonical.snapshot.layouts!
                : workingForm.roofModuleLayouts,
        );
        prepared.forEach(({ snapshot }) => {
            const orientation = snapshot.orientation;
            if (orientation && snapshot.modulesLayout?.length) {
                fills[orientation] = snapshot.modulesLayout;
            }
        });
    }

    const primaryOrientation =
        canonical?.snapshot.orientation ?? pickPrimaryFillOrientation(form);
    const usableSnapshots = snapshots.filter((row) => row.modules.length > 0);

    options?.onProgress?.('layout');
    const targetKwh = parseUsageKwh(workingForm.annualUsageKwh);
    let shortlist: RankedCandidate[];

    if (targetKwh > 0) {
        const usageFit = candidateFromIds(
            'usage-fit',
            idsForTargetUsage(workingForm),
            workingForm,
            primaryOrientation,
        );
        if (!usageFit) return empty;
        shortlist = [
            {
                ...enforceCandidateUsageWindow(usageFit, workingForm),
                fillOrientation: usageFit.fillOrientation ?? primaryOrientation,
                modulesLayout: usageFit.modulesLayout ?? modulesLayout,
            },
        ];
    } else {
        let ranked = collectHeuristicCandidates(workingForm, primaryOrientation);
        ranked.sort((a, b) => b.score - a.score);
        shortlist = ranked.slice(0, MAX_OPTIONS).map((draft) => ({
            ...draft,
            fillOrientation: draft.fillOrientation ?? primaryOrientation,
            modulesLayout: draft.modulesLayout ?? modulesLayout,
        }));
        if (shortlist.length === 0) {
            const draft = pickSingleHeuristicCandidate(workingForm, primaryOrientation);
            if (!draft) return empty;
            shortlist = [
                {
                    ...draft,
                    fillOrientation: draft.fillOrientation ?? primaryOrientation,
                    modulesLayout: draft.modulesLayout ?? modulesLayout,
                },
            ];
        }
    }

    options?.onProgress?.('gemini');
    const geminiPicks =
        shortlist.length > 0
            ? await designLayoutsWithGemini(workingForm, usableSnapshots)
            : null;

    options?.onProgress?.('simulate');
    const simulated = await simulateCandidatesInEmbed(
        shortlist,
        workingForm,
        fills,
        baselineGhostedIds,
    );
    const enforced =
        targetKwh > 0
            ? simulated.map((candidate) => enforceCandidateUsageWindow(candidate, workingForm))
            : simulated;
    const fullModuleCatalog = cloneLayouts(workingForm.roofModuleLayouts);
    const layoutOptions = sanitizePreContractAiDesignOptionsForUsageTarget(
        finalizeOptions(enforced, geminiPicks).map((option) => ({
            ...option,
            fullModuleLayouts: fullModuleCatalog,
            ghostedTreeIds: baselineGhostedIds,
        })),
        workingForm,
    );

    return { layoutOptions, treeCutOptions: [] };
}

/** Apply a ranked option onto the intake form. */
export function intakePatchFromAiDesign(
    option: PreContractAiDesignOption,
    siteTrees?: PreContractSiteTree[],
): Pick<PreContractIntakeForm, 'roofModuleLayouts' | 'roofSelections' | 'systemUsageKwh'> &
    Partial<Pick<PreContractIntakeForm, 'siteTrees'>> {
    const patch: Pick<PreContractIntakeForm, 'roofModuleLayouts' | 'roofSelections' | 'systemUsageKwh'> &
        Partial<Pick<PreContractIntakeForm, 'siteTrees'>> = {
        roofModuleLayouts: option.layouts,
        roofSelections: option.roofSelections,
        systemUsageKwh: option.annualKwh > 0 ? String(option.annualKwh) : '',
    };
    if (!siteTrees) return patch;
    const ghosted = new Set(option.ghostedTreeIds ?? []);
    patch.siteTrees = siteTrees.map((tree) => ({
        ...tree,
        ghosted: ghosted.has(tree.id),
    }));
    return patch;
}

const AI_OPTIONS_CACHE_KEY = 'preContractAiDesignOptionsCache.v24';

type PreContractAiOptionsCache = {
    layoutOptions: PreContractAiDesignOption[];
    treeCutOptions: PreContractAiDesignOption[];
    selectedId: string | null;
    selectedTab?: 'layouts' | 'trees';
};

/** Cache ranked options so returning from OneDraw does not regenerate them. */
export function persistPreContractAiOptionsCache(
    layoutOptions: PreContractAiDesignOption[],
    treeCutOptions: PreContractAiDesignOption[],
    selectedId: string | null,
    selectedTab: 'layouts' | 'trees' = 'layouts',
): void {
    try {
        const payload: PreContractAiOptionsCache = {
            layoutOptions,
            treeCutOptions,
            selectedId,
            selectedTab,
        };
        sessionStorage.setItem(AI_OPTIONS_CACHE_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function loadPreContractAiOptionsCache(): PreContractAiOptionsCache | null {
    try {
        const raw = sessionStorage.getItem(AI_OPTIONS_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PreContractAiOptionsCache> & {
            options?: PreContractAiDesignOption[];
        };
        const layoutOptions = Array.isArray(parsed.layoutOptions)
            ? parsed.layoutOptions
            : Array.isArray(parsed.options)
              ? parsed.options
              : [];
        if (layoutOptions.length === 0) return null;
        return {
            layoutOptions,
            treeCutOptions: Array.isArray(parsed.treeCutOptions) ? parsed.treeCutOptions : [],
            selectedId:
                typeof parsed.selectedId === 'string'
                    ? parsed.selectedId
                    : layoutOptions[0]?.id ?? null,
            selectedTab: parsed.selectedTab === 'trees' ? 'trees' : 'layouts',
        };
    } catch {
        return null;
    }
}

export function clearPreContractAiOptionsCache(): void {
    try {
        sessionStorage.removeItem(AI_OPTIONS_CACHE_KEY);
    } catch {
        /* ignore */
    }
    clearPreContractFullModuleLayouts();
}

/** True when a prior Run AI result is still cached for this session. */
export function hasPreContractAiOptionsCache(): boolean {
    return loadPreContractAiOptionsCache() != null;
}

/** Currently selected AI option from the session cache (layouts or tree-cut tab). */
export function resolveSelectedPreContractAiOption(): PreContractAiDesignOption | null {
    const cached = loadPreContractAiOptionsCache();
    if (!cached) return null;
    const tab = cached.selectedTab === 'trees' && cached.treeCutOptions.length > 0 ? 'trees' : 'layouts';
    const pool = tab === 'trees' ? cached.treeCutOptions : cached.layoutOptions;
    return pool.find((item) => item.id === cached.selectedId) ?? pool[0] ?? null;
}

/** Look up one cached AI option by id (layouts or tree-cut pools). */
export function resolvePreContractAiOptionById(optionId: string): PreContractAiDesignOption | null {
    const cached = loadPreContractAiOptionsCache();
    if (!cached || !optionId.trim()) return null;
    return (
        cached.layoutOptions.find((item) => item.id === optionId) ??
        cached.treeCutOptions.find((item) => item.id === optionId) ??
        null
    );
}
